import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { DaterangepickerDirective } from 'ngx-daterangepicker-material';
import { takeUntil } from 'rxjs/operators';
import { Moment } from 'moment';
import * as moment from 'moment';
import * as momentTz from 'moment-timezone';
import { AlertService, GatewayTransactionReportService, StorageService } from '../../../_services';
import { Form } from '../../../_forms/form';
import { Pager, User, getCRMOrderHtml } from '../../../_models';
import { config } from '../../../../config/config';
import { isEmpty } from 'lodash';
import {
  SummaryTableFields,
  SummaryTableData,
  DateRanges,
  SummaryData,
  CardType,
  CardIconGreyMap,
  TransactionType,
  SalesType,
  getTxnAmount,
} from '../transaction-reports';
import { downloadFromUrl } from '../../../_helpers/functions';
import { animate, state, style, transition, trigger } from '@angular/animations';

@Component({
  moduleId: module.id.toString(),
  selector: 'gateway-transaction-summary',
  templateUrl: './gateway-transaction-summary.component.html',
  styleUrls: ['./gateway-transaction-summary.component.scss'],
  animations: [
    trigger('expandCollapse', [
      state(
        'collapsed',
        style({
          height: '0px',
          opacity: 0,
          display: 'none',
        })
      ),
      state(
        'expanded',
        style({
          height: '*',
          opacity: 1,
          marginTop: '27px',
        })
      ),
      transition('collapsed <=> expanded', animate('400ms ease-in-out')),
    ]),
  ],
})
export class GatewayTransactionSummaryComponent extends Form implements OnInit {
  @ViewChild(DaterangepickerDirective, { static: false })
  pickerDirective: DaterangepickerDirective;

  currentUser: User;
  cardType = CardType;
  isTableLoading: boolean = false;
  isReportDownloading: boolean = false;
  maxDate = moment();
  date: { startDate: Moment; endDate: Moment };
  ranges = DateRanges;
  showMoreFilters = false;
  transactions: SummaryTableData[] = [];
  cardTypesList = [
    { id: CardType.Visa, name: 'Visa' },
    { id: CardType.MasterCard, name: 'Mastercard' },
    { id: CardType.Discover, name: 'Discover' },
    { id: CardType.Amex, name: 'Amex' },
  ];
  transactionTypesList = [
    { id: TransactionType.SALE, name: 'Sale' },
    { id: TransactionType.AUTHORIZE, name: 'Authorize' },
    { id: TransactionType.CAPTURE, name: 'Capture' },
    { id: TransactionType.VOID, name: 'Void' },
    { id: TransactionType.REFUND, name: 'Refund' },
  ];
  salesTypesList = [
    { id: SalesType.INITIAL, name: 'Initial' },
    { id: SalesType.REBILL, name: 'Rebill' },
    { id: SalesType.ONE_TIME, name: 'One-Time' },
  ];
  cardIconGreyMap = CardIconGreyMap;
  page = 1;
  pageCount = 1;
  pageSize = config.defaultPageSize;
  totalResultCount = 0;

  constructor(
    protected alertService: AlertService,
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected formBuilder: FormBuilder,
    public reportsService: GatewayTransactionReportService,
    protected storageService: StorageService
  ) {
    super(alertService, router, location);
  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      transaction_id: [null],
      order_id: [null],
      customer_email: [null, [Validators.email]],
      billing_phone: [
        null,
        [
          Validators.maxLength(12),
          Validators.pattern(/^\+1\d{10}$/), // +1 followed by exactly 10 digits
        ],
      ],
      customer_first_name: [null],
      customer_last_name: [null],
      action_type: [null],
      transaction_type: [null],
      sales_type: [null],
      gateway_name: [null],
      card_type: [null],
      address: [null],
      card_bin: [null, [Validators.minLength(6), Validators.maxLength(6)]],
      card_last_four: [null, [Validators.minLength(4), Validators.maxLength(4)]],
    });

    this.initializeDate();
    this.setCurrentUser();
    this.getReport();
  }

  getReport(resetPage: boolean = true) {
    this.submitted = true;
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.alertService.error('Please fix validation errors before proceeding.');
    } else {
      if (resetPage) {
        this.page = 1;
      }
      this.isTableLoading = true;
      this.reportsService
        .getFilteredReport({
          ...this.getReportParameters(),
          page: this.page,
          page_size: this.pageSize,
        })
        .pipe(takeUntil(this._destroy$))
        .subscribe(
          (data: Pager) => {
            this.pageCount = data.num_pages;
            this.totalResultCount = data.count;
            if (isEmpty(data.results)) {
              this.transactions = [];
              this.isTableLoading = false;
            } else {
              this.transactions = data.results.map((item: SummaryData) => {
                const dateTimeMoment = moment(item.transaction_attempted_date).tz('EST');
                const time = dateTimeMoment.format('hh:mm:ss A');
                const date = dateTimeMoment.format('MMM DD, YYYY');

                return {
                  date: date,
                  time: time,
                  merchantId: item.merchant_id,
                  firstName: item.customer_first_name,
                  lastName: item.customer_last_name,
                  txnType: item.transaction_type,
                  salesType: item.sales_type,
                  status: item.transaction_status,
                  txnId: item.transaction_id || null,
                  orderId: item.order_id || null,
                  platform: item.platform,
                  email: item.customer_email,
                  ccType: item.card_type,
                  cardFirstSix: item.card_bin,
                  cardLastFour: item.card_last_four,
                  amount: item.transaction_amount,
                  threeDsEnabled: item.three_ds_enabled,
                  attemptedDate: item.transaction_attempted_date,
                };
              });
              this.isTableLoading = false;
            }
          },
          (error) => {
            this.handleError(error);
            this.isTableLoading = false;
          }
        );
    }
  }

  onPageChange(params) {
    this.page = params.page;
    this.getReport(false);
  }

  getRangeStart(): number {
    let start = 0;
    if (this.totalResultCount > 0) {
      start = (this.page - 1) * this.pageSize + 1;
    }
    return start;
  }

  getRangeEnd(): number {
    return (this.page - 1) * this.pageSize + this.transactions.length;
  }

  downloadReport() {
    this.isReportDownloading = true;
    const req = {
      ...this.getReportParameters(),
      start_date_formatted: this.date.startDate.format('MMM Do, YYYY'),
      end_date_formatted: this.date.endDate.format('MMM Do, YYYY'),
    };

    this.reportsService.getDownloadReportData(req).subscribe(
      (data) => {
        downloadFromUrl(data.download_url);
        this.isReportDownloading = false;
      },
      (error) => {
        this.isReportDownloading = false;
        if (error.status === 404) {
          this.alertService.error('No reports to download');
          return;
        }
        this.alertService.error('An error occurred while downloading report');
      }
    );
  }

  onDateChange = (dateRange: { startDate: Moment; endDate: Moment }) => {
    this.date = {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };

    if (this.date.startDate && this.date.endDate) {
      this.getReport();
    }
  };

  initializeDate() {
    this.date = {
      startDate: moment().subtract(7, 'days').startOf('day'),
      endDate: moment().endOf('day'),
    };
    this.maxDate = this.date.endDate;
  }

  getTimezone(): string {
    return this.storageService.get('timzone') || momentTz.tz.guess();
  }

  getSelectedFilters() {
    const selectedFilters: any = {};
    for (const key in this.form.value) {
      const val = this.form.value[key];
      if (val) {
        // skips null, undefined, and empty string
        selectedFilters[key] = val;
      }
    }
    return selectedFilters;
  }

  getReportParameters() {
    if (!this.date.startDate || !this.date.endDate) {
      this.initializeDate();
    }

    return {
      accounts: [this.currentUser.account.id],
      customer_mode: false,
      date_filter_type: 'sessions',
      date_range: {
        startDate: moment(this.date.startDate).utc().format('YYYY-MM-DD HH:mm:ss'),
        endDate: moment(this.date.endDate).utc().format('YYYY-MM-DD HH:mm:ss'),
      },
      fields: [...SummaryTableFields, 'transaction_id', 'order_id'],
      filters: this.getSelectedFilters(),
      timezone: this.getTimezone(),
    };
  }

  showTransactionDetails(id: string, date: string) {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/checkouts/transaction-details', id, date]));
    window.open(url, '_blank');
  }

  getDisplayOrderId(orderId: string): string {
    let displayOrderId = orderId;
    if (orderId) {
      const tIndex = orderId.indexOf('T');
      if (tIndex > -1) {
        displayOrderId = orderId.slice(0, tIndex);
      } else {
        displayOrderId = orderId.slice(0, 10);
      }
    }
    return displayOrderId;
  }

  getOrderIdHtml(tx: SummaryTableData): string {
    return getCRMOrderHtml(this.getDisplayOrderId(tx.orderId), tx.orderId, tx.platform as any);
  }

  setCurrentUser() {
    this.currentUser = this.storageService.get('currentUser');
  }

  openDatepicker() {
    this.pickerDirective.open();
  }

  getMaskedCardNumber(lastFour: string): string {
    let maskedCardNumber = '--';
    if (lastFour) {
      maskedCardNumber = `***${lastFour}`;
    }
    return maskedCardNumber;
  }

  getMaskedMerchantId(merchantId: string): string {
    let maskedMerchantId = '--';
    if (merchantId) {
      const visibleLength = 6;
      let lastSix = merchantId;
      if (merchantId.length > visibleLength) {
        lastSix = merchantId.slice(merchantId.length - visibleLength);
      }
      maskedMerchantId = `***${lastSix}`;
    }
    return maskedMerchantId;
  }

  getAmount(amount: number, txnType: string): string {
    return getTxnAmount(amount, txnType);
  }
}
